/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyFrontOffice\AutobuyClient\src\Content\ProfilePage\ProfilePage.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyFrontOffice\AutobuyClient
 * Created Date: Tuesday September 19th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Wednesday, 25th September 2024 03:47:44
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { useLoaderData } from "react-router-dom";
import Styles from "Content/ProfilePage/ProfilePage.module.css";
import { useEffect, useRef, useState } from "react";
import PasswordInput from "Content/Password/PasswordInput/PasswordInput";
import InputText, { InputTextRefType } from "Content/InputText/InputText";
import HrStyled from "Theme/Component/HrStyled";
import ButtonCustom from "Theme/Component/ButtonCustom";
import UserService from "Services/Users/UserService";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import { ProfileDataType } from "Types/user.type";
import { useLang } from "Router/LangueProvider";
import { Checkbox, FormControlLabel } from "@mui/material";
import OfferTableUser from "Content/OfferUser/OfferUser";

function ProfilePage() {
    const loadData: ProfileDataType = useLoaderData() as ProfileDataType;
    const {translator} = useLang();
    const [openModalConfirmation, setOpenModalConfirmation] = useState<boolean>(false);
    const [isOkay, setIsOkay] = useState(false);
    const [isSendable, setIsSendable] = useState(false);
    const [commercialCallTime, setCommercialCallTime] = useState<Date | null>(loadData.CommercialCallTime ?? null);

    const userIdentifier = useRef<InputTextRefType> ([
        {
            key: "Email",
            text: loadData.Email,
            translateKey: "users.placeholder.email"
        }
    ]);

    const userAddress = useRef<InputTextRefType> ([
        {
            key: "Address",
            text: loadData.Address,
            translateKey: "users.placeholder.address"
        },
        {
            key: "PostalCode",
            text: loadData.PostalCode,
            translateKey: "users.placeholder.postal_code"
        },
        {
            key: "City",
            text: loadData.City,
            translateKey: "users.placeholder.city"
        },
        {
            key: "State",
            text: loadData.State,
            translateKey: "users.placeholder.state"
        },
        {
            key: "Country",
            text: loadData.Country,
            translateKey: "users.placeholder.country"
        }
    ]);

    const userData = useRef<InputTextRefType> ([
        {
            key: "LastName",
            text: loadData.LastName,
            translateKey: "users.placeholder.lastname"
        },
        {
            key: "FirstName",
            text: loadData.FirstName,
            translateKey: "users.placeholder.firstname"
        },
        {
            key: "Company",
            text: loadData.Company.Name,
            translateKey: "users.placeholder.company"
        },
    ]);

    const userPhone = useRef<InputTextRefType> ([
        {
            key: "Phone",
            text: loadData.Phone,
            translateKey: "users.placeholder.phoneNumber"
        },
        {
            key: "CellPhone",
            text: loadData.CellPhone,
            translateKey: "users.placeholder.cellPhone"
        }
    ]);

    const handleToggleModalConfirmation = (event: React.MouseEvent<HTMLElement>) => {
        setOpenModalConfirmation(false);
        setIsOkay(false);
    };

    const checkValues = () => {
        const myLastName = userData.current.find((val) => val.key === "LastName")?.text;
        const myFirstName = userData.current.find((val) => val.key === "FirstName")?.text;
        const myPhone = userPhone.current.find((val) => val.key === "Phone")?.text;
        const myCellPhone = userPhone.current.find((val) => val.key === "CellPhone")?.text;

        if ((myLastName && myLastName !== "" && myLastName !== loadData.LastName)
        || (myFirstName && myFirstName !== "" && myFirstName !== loadData.FirstName)
        || (myCellPhone && myCellPhone !== "" && myCellPhone !== loadData.CellPhone)
        || (commercialCallTime !== loadData.CommercialCallTime)
        || ((!(loadData.Phone === null && (myPhone === null || myPhone === undefined))) && myPhone !== loadData.Phone)) {
            setIsSendable(true);
        } else
            setIsSendable(false);
    };

    useEffect(() => {
        checkValues();
    }, [commercialCallTime]);

    const handleSaveInfo = () => {
        var updateVal: any = {};
        if (userData.current.find((val) => val.key === "LastName")?.text && userData.current.find((val) => val.key === "LastName")?.text !== loadData.LastName)
            updateVal["LastName"] = userData.current.find((val) => val.key === "LastName")!.text;

        if (userData.current.find((val) => val.key === "FirstName")?.text && userData.current.find((val) => val.key === "FirstName")?.text !== loadData.FirstName)
            updateVal["FirstName"] = userData.current.find((val) => val.key === "FirstName")!.text;

        if (userPhone.current.find((val) => val.key === "CellPhone")?.text && userPhone.current.find((val) => val.key === "CellPhone")?.text !== loadData.CellPhone)
            updateVal["CellPhone"] = userPhone.current.find((val) => val.key === "CellPhone")!.text;

        if (userPhone.current.find((val) => val.key === "Phone")?.text && userPhone.current.find((val) => val.key === "Phone")?.text !== loadData.Phone)
            updateVal["Phone"] = userPhone.current.find((val) => val.key === "Phone")!.text;
        else if (userPhone.current.find((val) => val.key === "Phone")?.text === undefined)
            updateVal["Phone"] = null;

        if (commercialCallTime !== loadData.CommercialCallTime)
            updateVal["CommercialCallTime"] = commercialCallTime;

        if (Object.values(updateVal).length !== 0) {
            UserService.updateUser(loadData.id, updateVal).then(() => {
                setIsOkay(true);
                setOpenModalConfirmation(true);
                setIsSendable(false);
            }).catch((error: any) => {
                console.error(error);
                setOpenModalConfirmation(true);
            });
        }
    };

    const handleChangePassword = (e: any, curPwd: string, newPassword: string) => {
        e.preventDefault();
        if (curPwd && newPassword) {
            UserService.updatePassword(curPwd, newPassword).then(() => {
                setIsOkay(true);
                setOpenModalConfirmation(true);
                setIsSendable(false);
            }).catch((error: any) => {
                console.error(error);
                setOpenModalConfirmation(true);
            });
        }
    };

    const handleChange = (e: any) => {
        if (e.target.checked)
            setCommercialCallTime(new Date());
        else
            setCommercialCallTime(null);
    };

    return (
        <div className={`${Styles.profileHolder}`}>
            <h2 className={`${Styles.underline}`}>{translator.decode("users.infos")}</h2>
            <InputText ref={userIdentifier} titleClass={`${Styles.titleInfo} ${Styles.underline}`} holderClass={Styles.flex} disabled={true} title={"users.identifier"} checkValues={checkValues} />
            <InputText ref={userData} titleClass={`${Styles.titleInfo} ${Styles.underline}`} holderClass={Styles.flex} disabled={false} title={"users.personnalInfos"} checkValues={checkValues} />
            <InputText ref={userPhone} titleClass={`${Styles.titleInfo} ${Styles.underline}`} holderClass={Styles.flex} disabled={false} title={"users.contactPhone"} checkValues={checkValues} />
            <InputText ref={userAddress} titleClass={`${Styles.titleInfo} ${Styles.underline}`} holderClass={`${Styles.flex}`} disabled={true} title={"users.address"} checkValues={checkValues} />
            <HrStyled margin={"1rem auto"} bgColor="" />
            <h2 className={`${Styles.underline}`}>{translator.decode("users.parameters")}</h2>
            <FormControlLabel className={`${Styles.checkBox}`} control={<Checkbox defaultChecked={commercialCallTime ? true : false} onClick={(e) => handleChange(e)} />} label={translator.decode("agreement.commercial")} />
            <ButtonCustom disabled={!isSendable} className={Styles.btnSend} type="submit" onClick={handleSaveInfo} >{translator.decode("dynamic.store")}</ButtonCustom>
            <HrStyled margin={"1rem auto"} bgColor="" />
            <h2 className={`${Styles.underline}`}>{translator.decode("users.bought_cars")}</h2>
            <OfferTableUser buy={true} />
            <HrStyled margin={"1rem auto"} bgColor="" />
            <h2 className={`${Styles.underline}`}>{translator.decode("users.changePassword")}</h2>
            <PasswordInput HandleSubmit={handleChangePassword} askCurrentPwd={true} />
            <ModalConfirmation show={openModalConfirmation} onClose={handleToggleModalConfirmation} closeModal={handleToggleModalConfirmation} isOkay={isOkay} themeColor={""} />
        </div>
    )
}

export default ProfilePage;
